import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { academyService } from '../../services/academy';
import { useAcademySlug } from '../../hooks/useAcademySlug';
import type { Academy } from '@court-sync/shared';
import { AcademyBrandingProvider } from '@/contexts/AcademyBrandingContext';
import { ForgotPasswordForm } from '../../components/auth/ForgotPasswordForm';
import {
  buildAcademyLoginPath,
  buildPasswordResetContinueUrls,
  getAcademyHostMode,
} from '../../lib/academyPaths';
import { requestPasswordResetWithFallback } from './academyAuthHandlers';
import { isStarterMemberAuthBlockedAcademy } from '../../utils/publicBrowseMode';
import { StarterMemberAuthNotFound } from '../../components/public/StarterMemberAuthNotFound';

export default function AcademyForgotPasswordPage() {
  const academySlug = useAcademySlug();
  const [searchParams] = useSearchParams();
  const [academy, setAcademy] = useState<Academy | null>(null);
  const [loadingAcademy, setLoadingAcademy] = useState(() => Boolean(academySlug));
  const [academyError, setAcademyError] = useState<string | null>(null);

  const returnUrl = searchParams.get('returnUrl') ?? undefined;
  const dashboardType = searchParams.get('dashboard') ?? undefined;

  useEffect(() => {
    if (!academySlug) return;
    academyService
      .getAcademyBySlug(academySlug)
      .then((a) => {
        setAcademy(a);
        setAcademyError(null);
      })
      .catch(() => {
        setAcademy(null);
        setAcademyError('Academy not found');
      })
      .finally(() => setLoadingAcademy(false));
  }, [academySlug]);

  const loginPath = academy
    ? (() => {
        const base = buildAcademyLoginPath(academy, returnUrl, getAcademyHostMode());
        if (!dashboardType) return base;
        const sep = base.includes('?') ? '&' : '?';
        return `${base}${sep}dashboard=${encodeURIComponent(dashboardType)}`;
      })()
    : '/login';

  const handleForgotSubmit = useCallback(
    async (email: string) => {
      if (!academy) return;
      const { primary, fallback } = buildPasswordResetContinueUrls(academy, returnUrl, getAcademyHostMode());
      await requestPasswordResetWithFallback(email, primary, fallback);
    },
    [academy, returnUrl]
  );

  if (!academySlug) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <p className="text-red-600 dark:text-red-400 mb-4">Academy not found</p>
        <Link to="/login" className="text-primary-600 hover:text-primary-500 dark:text-primary-400">
          Go to main login
        </Link>
      </div>
    );
  }

  if (loadingAcademy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <p className="text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  if (academyError || !academy) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <p className="text-red-600 dark:text-red-400 mb-4">{academyError}</p>
        <Link to="/login" className="text-primary-600 hover:text-primary-500 dark:text-primary-400">
          Go to main login
        </Link>
      </div>
    );
  }

  if (isStarterMemberAuthBlockedAcademy(academy)) {
    return <StarterMemberAuthNotFound academy={academy} academySlug={academySlug} />;
  }

  return (
    <AcademyBrandingProvider primaryColor={academy.primaryColor} secondaryColor={academy.secondaryColor}>
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-tennis-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              {academy.logoUrl ? (
                <img
                  src={academy.logoUrl}
                  alt={academy.name}
                  className="h-16 w-auto mx-auto mb-4 object-contain"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="flex justify-center mb-6">
                  <img src="/icon.png" alt="CourtSync" className="h-[60px] w-[60px]" width={60} height={60} />
                </div>
              )}
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{academy.name}</h1>
              <p className="text-gray-600 dark:text-gray-400">Reset your password</p>
            </div>

            <ForgotPasswordForm loginPath={loginPath} onSubmit={handleForgotSubmit} />
          </div>
        </div>
      </div>
    </AcademyBrandingProvider>
  );
}
